/**
 * Metrics Hooks with React Query
 *
 * Replaces all previous metrics hooks with a unified React Query approach:
 * - useMetrics -> useMetrics (list with filters)
 * - useMetric -> useMetric (single metric)
 * - useMetricTrend -> useMetricTrend (trend data)
 * - usePublicMetrics -> usePublicMetrics (public metrics)
 * - useCreateMetric, useUpdateMetric, useDeleteMetric -> mutations in useMetrics
 * - useBulkMetrics -> bulk mutations in useMetrics
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { metricsService } from '../services/metrics.service';
import type {
  PublicMetric,
  Metric,
  MetricTrend,
  CreateMetricDTO,
  UpdateMetricDTO,
  MetricFilters,
} from '@lnr-v2/types';

// Re-export types for backwards compatibility
export type {
  PublicMetric,
  Metric,
  MetricTrend,
  CreateMetricDTO,
  UpdateMetricDTO,
  MetricFilters,
};

/**
 * Hook for public metrics (no authentication required)
 */
export function usePublicMetrics(options: { refreshInterval?: number } = {}) {
  const { refreshInterval } = options;

  const {
    data: metrics = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: queryKeys.metrics.dashboard(),
    queryFn: metricsService.getPublic,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: refreshInterval || false,
  });

  const refetch = useCallback(async () => {
    // This will be handled by React Query
  }, []);

  return {
    metrics,
    loading,
    error: error ? (error as Error) : null,
    refetch,
  };
}

/**
 * Hook for fetching metrics list with filters
 */
export function useMetrics(filters?: MetricFilters) {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.metrics.dashboard(),
    queryFn: () => metricsService.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });

  const metrics = data?.metrics || [];
  const total = data?.total || 0;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: metricsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<UpdateMetricDTO>;
    }) => metricsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: metricsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: metricsService.bulkDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: ({
      ids,
      updates,
    }: {
      ids: string[];
      updates: Partial<UpdateMetricDTO>;
    }) => metricsService.bulkUpdate(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  const clearError = useCallback(() => {
    // Error clearing is handled by React Query
  }, []);

  return {
    metrics,
    total,
    loading,
    error: error ? (error as Error) : null,
    refetch: async () => {
      await refetch();
    },
    clearError,
    // Mutations
    createMetric: async (data: CreateMetricDTO) => {
      return await createMutation.mutateAsync(data);
    },
    updateMetric: async (id: string, data: Partial<UpdateMetricDTO>) => {
      return await updateMutation.mutateAsync({ id, data });
    },
    deleteMetric: async (id: string) => {
      await deleteMutation.mutateAsync(id);
    },
    bulkDelete: async (ids: string[]) => {
      await bulkDeleteMutation.mutateAsync(ids);
    },
    bulkUpdate: async (ids: string[], updates: Partial<UpdateMetricDTO>) => {
      return await bulkUpdateMutation.mutateAsync({ ids, updates });
    },
  };
}

/**
 * Hook for fetching a single metric
 */
export function useMetric(id: string | undefined) {
  const queryClient = useQueryClient();

  const {
    data: metric = null,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: id ? queryKeys.metrics.dashboard() : [],
    queryFn: () => (id ? metricsService.getById(id) : null),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (updates: Partial<Metric>) => {
      if (!id) throw new Error('No metric ID provided');
      return metricsService.update(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('No metric ID provided');
      return metricsService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
    },
  });

  return {
    metric,
    loading,
    error: error ? (error as Error) : null,
    refetch: async () => {
      await refetch();
    },
    updateMetric: async (updates: Partial<Metric>) => {
      return await updateMutation.mutateAsync(updates);
    },
    deleteMetric: async () => {
      await deleteMutation.mutateAsync();
    },
  };
}

/**
 * Hook for fetching metric trend data
 */
export function useMetricTrend(
  id: string | undefined,
  period: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month'
) {
  const {
    data: trend = null,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: id ? queryKeys.metrics.dashboard() : [],
    queryFn: () => (id ? metricsService.getTrend(id, period) : null),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    trend,
    loading,
    error: error ? (error as Error) : null,
    refetch: async () => {
      await refetch();
    },
  };
}

/**
 * Hook for creating metrics
 * @deprecated Use useMetrics().createMetric instead
 */
export function useCreateMetric() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: metricsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
    },
  });

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    createMetric: async (data: CreateMetricDTO) => {
      return await mutation.mutateAsync(data);
    },
    loading: mutation.isPending,
    error,
    clearError,
  };
}

/**
 * Hook for updating metrics
 * @deprecated Use useMetrics().updateMetric instead
 */
export function useUpdateMetric() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<UpdateMetricDTO>;
    }) => metricsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
    },
  });

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    updateMetric: async (id: string, data: Partial<UpdateMetricDTO>) => {
      return await mutation.mutateAsync({ id, data });
    },
    loading: mutation.isPending,
    error,
    clearError,
  };
}

/**
 * Hook for deleting metrics
 * @deprecated Use useMetrics().deleteMetric instead
 */
export function useDeleteMetric() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: metricsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
    },
  });

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    deleteMetric: async (id: string) => {
      await mutation.mutateAsync(id);
    },
    loading: mutation.isPending,
    error,
    clearError,
  };
}

/**
 * Hook for bulk operations on metrics
 * @deprecated Use useMetrics().bulkDelete / bulkUpdate instead
 */
export function useBulkMetrics() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  const bulkDeleteMutation = useMutation({
    mutationFn: metricsService.bulkDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({
      ids,
      updates,
    }: {
      ids: string[];
      updates: Partial<UpdateMetricDTO>;
    }) => metricsService.bulkUpdate(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
    },
  });

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    bulkDelete: async (ids: string[]) => {
      await bulkDeleteMutation.mutateAsync(ids);
    },
    bulkUpdate: async (ids: string[], updates: Partial<UpdateMetricDTO>) => {
      return await bulkUpdateMutation.mutateAsync({ ids, updates });
    },
    loading: bulkDeleteMutation.isPending || bulkUpdateMutation.isPending,
    error,
    clearError,
  };
}
